import { useMemo, Dispatch, SetStateAction } from 'react'
import { Button } from 'primereact/button'
import { useTranslation } from 'react-i18next'

// local imports
import { IFilteredCoiffeurList, IFilteredCoiffeurSalonList } from 'types/CoiffeurType'
import DateUtil from 'utils/DateUtil'
import DatePickerIcon from 'components/ui/DatePickerIcon'
import CoiffeurPhoto from '../coiffeur-search-modal/CoiffeurPhoto'
import { HeaderInfo } from './styled'

interface ModalHeaderProps {
  subServiceId: number
  salon: IFilteredCoiffeurSalonList
  availableDateRange: { start: number; end: number }
  filterDate: Date
  setFilterDate: Dispatch<SetStateAction<Date>>
  setSelectedCoiffeur: Dispatch<SetStateAction<IFilteredCoiffeurList | undefined>>
  selectedCoiffeur: IFilteredCoiffeurList | undefined
  coiffeurs: IFilteredCoiffeurList[] | []
  setTableWeek: Dispatch<SetStateAction<{ start: Date; end: Date }>>
  selectedDateStr?: string
}

export default function ReservationModalHeader({
  subServiceId,
  salon,
  availableDateRange: availableDates,
  selectedDateStr,
  filterDate,
  setFilterDate,
  setTableWeek,
  selectedCoiffeur,
  coiffeurs,
  setSelectedCoiffeur,
}: ModalHeaderProps) {
  const { t } = useTranslation()
  const { id, name, surname, employeeId } = selectedCoiffeur || {}
  const {
    name: SalonName,
    listCoiffeurSaloonSpec: salonSpecs,
    floor: { name: floorName, locationName: LocationName, districtName: DistrictName, cityName: CityName, countryName: CountryName, blockName: BlockName },
  } = salon || {}

  // Variables
  const length = coiffeurs?.length
  const btnDisabled = length <= 1
  const currentCoiffeurIndex = useMemo(() => coiffeurs.findIndex((coif) => coif.id === id), [coiffeurs, id])
  const locationName = `${LocationName}, ${DistrictName}, ${CityName}, ${CountryName}, ${BlockName} ${floorName}`

  /* Main - Sub Services Options */
  const subService = salonSpecs
    .filter((ss) => ss.coiffeurSubService.id === subServiceId)
    .map(({ coiffeurSubService, price }) => ({
      ...coiffeurSubService,
      price: price,
    }))

  /* Price Info variables */
  const price = subService[0]?.price
  const serviceName = subService[0]?.name

  /* Coiffeur Slider */
  const nextSlide = () =>
    currentCoiffeurIndex === length - 1
      ? setSelectedCoiffeur(coiffeurs[0])
      : setSelectedCoiffeur(coiffeurs[currentCoiffeurIndex + 1])
  const prevSlide = () =>
    currentCoiffeurIndex === 0
      ? setSelectedCoiffeur(coiffeurs[length - 1])
      : setSelectedCoiffeur(coiffeurs[currentCoiffeurIndex - 1])

  /* Handle Reservation Date change input */
  const handleDateChange = (e: any) => {
    if (e.value) {
      const { start, end } = DateUtil.getWeekStartEndFromDate(e.value)
      setTableWeek({ start, end })
      setFilterDate(e.value)
    }
  }

  return (
    <HeaderInfo>
      <div className="coiffeur-info">
        <div className="slider-left-arrow">
          <Button
            id="coiffeurPrev"
            disabled={btnDisabled}
            onClick={prevSlide}
            icon="pi pi-chevron-left"
            className="p-button-plain p-button-rounded p-button-text"
          />
        </div>

        <div className="coiffeur-photo">
          <CoiffeurPhoto coiffeurEmployeeId={employeeId || 0} IsAbsolute={false} size={[54, 54]} />
        </div>

        <div className="coiffeur-summary">
          <span className="coiffeur-name-surname">{`${name} ${surname}`}</span>
          <span className="coiffeur-info-text">
            <span className="fw-bold">{SalonName}</span>
          </span>
          <span className="coiffeur-info-text">{locationName}</span>
          <span className="coiffeur-info-text">
            <span className="fw-bold"> {t('coiffeur-res-modal.service-price')}</span> : {`${serviceName} / ${price}₺`}
          </span>
        </div>

        <div className="slider-right-arrow">
          <Button
            id="coiffeurNext"
            disabled={btnDisabled}
            onClick={nextSlide}
            icon="pi pi-chevron-right"
            className="p-button-plain p-button-rounded p-button-text"
          />
        </div>
      </div>

      <div className="d-flex flex-column align-items-center justify-content-end">
        {!!selectedDateStr ? (
          <div className="calendar-notify">{t('calendar.selected-day-info-text', { date: selectedDateStr })}</div>
        ) : null}
      </div>

      <div className="appointment-date">
        <DatePickerIcon
          idName="coiffeurModalReservationDate"
          showOtherMonths
          selectOtherMonths
          showOnFocus
          appendTo={document.body}
          disabledDays={[]}
          defaultDate={filterDate}
          minDate={DateUtil.getPastDate(availableDates.start)}
          maxDate={DateUtil.getFutureDate(availableDates.end)}
          label={t('coiffeur-res-modal.reservation-date')}
          onSelectedDateChange={handleDateChange}
          onFocus={() => {}}
        />
      </div>
    </HeaderInfo>
  )
}
